import { useState } from 'react'

import { getNestedValue, NestedObject } from '../utils/getNestedValue'

export type OrderByItem = NestedObject
export type OrderBy = OrderByItem | OrderByItem[] | null

export function useOrderBy(initial?: OrderBy) {
  const [orderBy, setOrderBy] = useState<OrderBy>(initial || null)

  // Função auxiliar para criar objeto aninhado a partir de uma string com pontos
  const createNestedObject = (path: string, value: string): NestedObject => {
    const keys = path.split('.')
    return keys.reduceRight(
      (acc: NestedObject | string, key: string): NestedObject => {
        return { [key]: acc }
      },
      value as NestedObject | string,
    ) as NestedObject
  }

  // Função auxiliar para verificar se um objeto está vazio ou só tem objetos vazios
  const isEmptyOrOnlyEmptyObjects = (obj: NestedObject): boolean => {
    for (const key in obj) {
      const value = obj[key]
      if (typeof value === 'string') {
        return false
      }
      if (typeof value === 'object' && !isEmptyOrOnlyEmptyObjects(value)) {
        return false
      }
    }
    return true
  }

  // Função auxiliar para encontrar um campo em um objeto NestedObject
  const findFieldInObject = (
    obj: NestedObject,
    field: string,
  ): string | undefined => {
    if (field.includes('.')) {
      return getNestedValue(obj, field)
    }
    return typeof obj[field] === 'string' ? obj[field] : undefined
  }

  // Função auxiliar para contar o número total de campos em um OrderBy
  const countTotalFields = (obj: NestedObject): number => {
    let count = 0
    for (const key in obj) {
      const value = obj[key]
      if (typeof value === 'string') {
        count++
      } else if (typeof value === 'object') {
        count += countTotalFields(value)
      }
    }
    return count
  }

  // Função auxiliar para normalizar o resultado (objeto se 1 campo, array se múltiplos)
  const normalizeOrderBy = (orderBy: OrderBy): OrderBy => {
    if (orderBy === null) return null

    if (Array.isArray(orderBy)) {
      if (orderBy.length === 0) return null
      if (orderBy.length === 1) return orderBy[0]
      return orderBy
    }

    // É um objeto único
    const totalFields = countTotalFields(orderBy)
    if (totalFields === 0) return null
    if (totalFields === 1) return orderBy

    // Se o objeto tem múltiplos campos, convertemos para array
    const result: NestedObject[] = []
    for (const key in orderBy) {
      const value = orderBy[key]
      if (typeof value === 'string') {
        result.push({ [key]: value })
      } else if (typeof value === 'object') {
        // Para objetos aninhados, mantemos a estrutura
        result.push({ [key]: value })
      }
    }

    return result.length <= 1 ? result[0] || null : result
  }

  // Função auxiliar para remover propriedade aninhada
  const removeNestedProperty = (
    obj: NestedObject,
    path: string,
  ): NestedObject => {
    const keys = path.split('.')
    const result = { ...obj }

    if (keys.length === 1) {
      delete result[keys[0]]
      return result
    }

    const lastKey = keys.pop()!
    let current: NestedObject = result

    // Navegar até o penúltimo objeto e clonar o caminho
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (!current[key] || typeof current[key] === 'string') {
        return result
      }
      current[key] = { ...(current[key] as NestedObject) }
      current = current[key] as NestedObject
    }

    if (lastKey && current) {
      delete current[lastKey]
    }

    return result
  }

  // Função auxiliar para definir propriedade aninhada
  const setNestedProperty = (
    obj: NestedObject,
    path: string,
    value: string,
  ): NestedObject => {
    const keys = path.split('.')
    const result = { ...obj }

    if (keys.length === 1) {
      result[keys[0]] = value
      return result
    }

    const lastKey = keys.pop()!
    let current: NestedObject = result

    // Navegar até o penúltimo objeto, criando o caminho se necessário
    for (const key of keys) {
      if (!current[key]) {
        current[key] = {}
      } else if (typeof current[key] === 'string') {
        current[key] = {}
      } else {
        current[key] = { ...(current[key] as NestedObject) }
      }
      current = current[key] as NestedObject
    }

    current[lastKey] = value
    return result
  }

  // Função auxiliar para encontrar um campo no OrderBy (objeto ou array)
  const findFieldInOrderBy = (
    orderBy: OrderBy,
    field: string,
  ): { value: string; index?: number } | null => {
    if (orderBy === null) return null

    if (Array.isArray(orderBy)) {
      for (let i = 0; i < orderBy.length; i++) {
        const value = findFieldInObject(orderBy[i], field)
        if (value) {
          return { value, index: i }
        }
      }
      return null
    }

    const value = findFieldInObject(orderBy, field)
    return value ? { value } : null
  }

  const handleChangeOrder = (order: string, by: 'asc' | 'desc') => {
    setOrderBy((prevOrderBy) => {
      // Se o orderBy anterior for null, criamos um novo objeto
      if (prevOrderBy === null) {
        const newOrderBy = order.includes('.')
          ? createNestedObject(order, by)
          : { [order]: by }
        return normalizeOrderBy(newOrderBy)
      }

      // Procuramos se o campo já existe
      const existingField = findFieldInOrderBy(prevOrderBy, order)

      // Se o campo já existir com o mesmo valor, removemos
      if (existingField && existingField.value === by) {
        if (Array.isArray(prevOrderBy)) {
          const newArray = [...prevOrderBy]
          if (typeof existingField.index === 'number') {
            // Removemos o item do array se for um campo único
            const item = newArray[existingField.index]
            if (order.includes('.')) {
              const updatedItem = removeNestedProperty(item, order)
              if (isEmptyOrOnlyEmptyObjects(updatedItem)) {
                newArray.splice(existingField.index, 1)
              } else {
                newArray[existingField.index] = updatedItem
              }
            } else {
              const updatedItem = { ...item }
              delete updatedItem[order]
              if (isEmptyOrOnlyEmptyObjects(updatedItem)) {
                newArray.splice(existingField.index, 1)
              } else {
                newArray[existingField.index] = updatedItem
              }
            }
          }
          return normalizeOrderBy(newArray.length === 0 ? null : newArray)
        } else {
          // É um objeto único
          const newOrderBy = order.includes('.')
            ? removeNestedProperty(prevOrderBy, order)
            : (() => {
                const result = { ...prevOrderBy }
                delete result[order]
                return result
              })()

          return normalizeOrderBy(
            Object.keys(newOrderBy).length === 0 ||
              isEmptyOrOnlyEmptyObjects(newOrderBy)
              ? null
              : newOrderBy,
          )
        }
      }

      // Caso contrário, atualizamos ou adicionamos a ordenação
      if (Array.isArray(prevOrderBy)) {
        const newArray = [...prevOrderBy]

        if (existingField && typeof existingField.index === 'number') {
          // Atualizamos o item existente
          const item = newArray[existingField.index]
          const updatedItem = order.includes('.')
            ? setNestedProperty(item, order, by)
            : { ...item, [order]: by }
          newArray[existingField.index] = updatedItem
        } else {
          // Adicionamos um novo item
          const newItem = order.includes('.')
            ? createNestedObject(order, by)
            : { [order]: by }
          newArray.push(newItem)
        }

        return normalizeOrderBy(newArray)
      } else {
        // É um objeto único - vamos adicionar o novo campo
        const updatedObject = order.includes('.')
          ? setNestedProperty(prevOrderBy, order, by)
          : { ...prevOrderBy, [order]: by }

        return normalizeOrderBy(updatedObject)
      }
    })
  }

  return {
    orderBy,
    handleChangeOrder,
  }
}
